import { OpenFeature, ProviderEvents, ProviderStatus } from '@openfeature/web-sdk';

export const AUTH0_ENABLED_FLAG_KEY = 'corinsights_auth0_enabled';

/**
 * Resolves `corinsights_auth0_enabled` off the app's own OpenFeature client — the SAME provider
 * every other flag in this app reads (ConfigCat in real environments, the offline in-memory
 * provider for the fully-local stack — see app.module.ts's createFeatureFlagProvider()). There is
 * deliberately no separate ConfigCat connection here; the previous design opened its own raw
 * @configcat/sdk client, which meant two independent connections/fetches for the same tenant.
 *
 * WAITS FOR PROVIDER READY (bounded by timeoutMs). A provider that hasn't finished its initial
 * fetch reports NOT_READY, and reading a flag in that state returns the caller's default rather
 * than the real value — reading immediately after `OpenFeature.setProvider()` would silently treat
 * a flag that is actually 'on' as 'off'. This waits for the Ready/Error event once, capped at
 * timeoutMs, then reads exactly once. It never rejects and never hangs indefinitely: on timeout or
 * a provider error it just reads whatever the client currently has (falling back to `fallback` if
 * that's still nothing) — a slow or unreachable ConfigCat can only ever cost a same-session
 * fallback to CorView, never a blocked login.
 *
 * WHY THIS CAN RUN LAZILY, UNLIKE THE OLD PRE-BOOTSTRAP DESIGN. auth-config.module.ts calls this
 * from a StsConfigLoader factory, which Angular only invokes once something actually needs the
 * OIDC config (e.g. the first checkAuth()) — well after bootstrapModule() has finished constructing
 * the full DI tree, so OpenFeatureModule's provider (registered synchronously in app.module.ts's own
 * @NgModule decorator, evaluated before Angular's runtime injector phase even starts) is always
 * already registered by the time this runs. That ordering guarantee is what let main.ts go back to
 * a plain bootstrapModule(AppModule) call — no pre-bootstrap resolve()/freeze step needed anymore.
 */
export function resolveAuth0Enabled(fallback = false, timeoutMs = 3000): Promise<boolean> {
  const client = OpenFeature.getClient();

  const settled =
    client.providerStatus === ProviderStatus.NOT_READY
      ? new Promise<void>((resolve) => {
          const controller = new AbortController();
          const done = () => {
            controller.abort();
            resolve();
          };
          client.addHandler(ProviderEvents.Ready, done, { signal: controller.signal });
          client.addHandler(ProviderEvents.Error, done, { signal: controller.signal });
        })
      : Promise.resolve();

  return Promise.race([
    settled,
    new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
  ]).then(() => client.getBooleanValue(AUTH0_ENABLED_FLAG_KEY, fallback));
}